import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, DownloadSimple, ClipboardText } from '@phosphor-icons/react';
import { useInView } from 'react-intersection-observer';
import toast from 'react-hot-toast';

import { Spinner, EmptyState, Button } from '@/components/ui';
import { ResponseTable } from '@/components/features/ResponseTable/ResponseTable';
import { useForm, useFormResponses } from '@/api';
import { useAuthStore } from '@/stores';
import { API_ROUTES, ROUTES, buildResponseDetailUrl } from '@/constants';
import { api } from '@/lib/axios';

import styles from './FormResponses.module.css';

export function FormResponses() {
    const { userSlug, formSlug } = useParams<{ userSlug: string; formSlug: string }>();
    const navigate = useNavigate();
    const { getUser } = useAuthStore();
    const user = getUser();

    const { data: form, isLoading: formLoading, error: formError } = useForm(formSlug || '');
    const {
        data,
        isLoading: responsesLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = useFormResponses(formSlug || '');

    const { ref: loadMoreRef } = useInView({
        onChange: (inView) => {
            if (inView && hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        },
    });

    useEffect(() => {
        if (form) {
            document.title = `Respuestas — ${form.title} — Ratio`;
        }
    }, [form]);

    const isOwner = form && user && form.author.id === user.id;

    const handleExport = async () => {
        if (!formSlug) return;
        try {
            const response = await api.get(API_ROUTES.formExport(formSlug), {
                responseType: 'blob',
            });
            const blob = new Blob([response.data as BlobPart], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `formulario-${formSlug}-respuestas.csv`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Exportacion descargada');
        } catch {
            toast.error('No se pudo exportar las respuestas');
        }
    };

    if (formLoading) {
        return (
            <div className={styles.loading}>
                <Spinner size="lg" />
            </div>
        );
    }

    if (formError || !form) {
        return (
            <div className={styles.errorPage}>
                <h2 className={styles.errorTitle}>Formulario no encontrado</h2>
                <Button variant="primary" onClick={() => navigate(ROUTES.feed)}>
                    Volver al inicio
                </Button>
            </div>
        );
    }

    if (!isOwner) {
        return (
            <div className={styles.errorPage}>
                <h2 className={styles.errorTitle}>Acceso restringido</h2>
                <p className={styles.errorText}>Solo el autor puede ver las respuestas.</p>
                <Button variant="primary" onClick={() => navigate(ROUTES.feed)}>
                    Volver al inicio
                </Button>
            </div>
        );
    }

    const responses = data?.pages.flatMap((page) => page.results) ?? [];
    const totalCount = data?.pages[0]?.count ?? 0;
    const resolvedUserSlug = userSlug || form.author.slug;

    return (
        <div className={styles.page}>
            <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Volver">
                <ArrowLeft size={18} />
                Volver
            </button>

            <div className={styles.header}>
                <div className={styles.headerInfo}>
                    <h1 className={styles.pageTitle}>Respuestas</h1>
                    <p className={styles.formName}>{form.title}</p>
                </div>
                <button
                    type="button"
                    className={styles.exportBtn}
                    onClick={handleExport}
                    aria-label="Exportar respuestas como CSV"
                >
                    <DownloadSimple size={16} weight="bold" />
                    Exportar CSV
                </button>
            </div>

            {/* Summary stats */}
            <div className={styles.statsRow}>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>{totalCount}</span>
                    <span className={styles.statLabel}>Total respuestas</span>
                </div>
                {responses.length > 0 && (
                    <div className={styles.statCard}>
                        <span className={styles.statValue}>
                            {new Date(responses[0].created).toLocaleDateString('es', {
                                day: 'numeric',
                                month: 'short',
                            })}
                        </span>
                        <span className={styles.statLabel}>Ultima respuesta</span>
                    </div>
                )}
            </div>

            {responsesLoading ? (
                <div className={styles.loadingInline}>
                    <Spinner size="md" />
                </div>
            ) : responses.length === 0 ? (
                <EmptyState
                    icon={<ClipboardText weight="duotone" />}
                    title="Sin respuestas aun"
                    description="Comparte el formulario para empezar a recibir respuestas"
                    actionLabel="Compartir"
                    onAction={() => navigate(`/${form.author.slug}/f/${form.slug}/compartir`)}
                />
            ) : (
                <>
                    <ResponseTable
                        responses={responses}
                        fields={form.fields}
                        onRowClick={(response) =>
                            navigate(buildResponseDetailUrl(resolvedUserSlug, form.slug, response.id))
                        }
                    />
                </>
            )}

            {hasNextPage && (
                <div ref={loadMoreRef} className={styles.loadMore}>
                    {isFetchingNextPage ? (
                        <Spinner size="sm" />
                    ) : (
                        <button
                            type="button"
                            className={styles.loadMoreBtn}
                            onClick={() => fetchNextPage()}
                        >
                            Cargar mas
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
